const STORAGE_KEY = 'itk_accounts_v1';
const ACTIVE_KEY = 'itk_active_account_v1';

const LEGACY_DEFAULTS = {
  token: '',
  userID: '',
  userName: 'Имя пользователя',
  userType: 'individual',
  mainCss: 'main-page-phys',
  agreementNumber: '',
  personalAccount: '',
  chatBadge: 'false',
  notificationBadge: 'false',
  chatActive: 'true',
  agreementAccepted: 'false'
};

const canUseStorage = () => typeof localStorage !== 'undefined';

const notifyAccountsChanged = () => {
  if (typeof window === 'undefined' || !window.dispatchEvent || typeof CustomEvent !== 'function') {
    return;
  }
  window.dispatchEvent(new CustomEvent('accounts:updated'));
};

const normalizeLogin = (login) => String(login || '').trim().toLowerCase();

const safeParse = (raw) => {
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
};

const readAccounts = () => {
  if (!canUseStorage()) {
    return [];
  }
  const parsed = safeParse(localStorage.getItem(STORAGE_KEY));
  if (!Array.isArray(parsed)) {
    return [];
  }
  return parsed.filter(item => item && typeof item === 'object' && item.id);
};

const writeAccounts = (accounts) => {
  if (!canUseStorage()) {
    return;
  }
  if (!Array.isArray(accounts) || accounts.length === 0) {
    localStorage.removeItem(STORAGE_KEY);
  } else {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(accounts));
  }
  notifyAccountsChanged();
};

const getActiveAccountId = () => {
  if (!canUseStorage()) {
    return null;
  }
  return localStorage.getItem(ACTIVE_KEY);
};

const setActiveAccountId = (id) => {
  if (!canUseStorage()) {
    return;
  }
  if (!id) {
    localStorage.removeItem(ACTIVE_KEY);
  } else {
    localStorage.setItem(ACTIVE_KEY, id);
  }
  notifyAccountsChanged();
};

const generateAccountId = ({userID, loginNormalized}) => {
  if (userID) {
    return `uid:${userID}`;
  }
  if (loginNormalized) {
    return `login:${loginNormalized}`;
  }
  return `acc:${Date.now()}_${Math.floor(Math.random() * 1000000)}`;
};

const findAccountIndex = (accounts, {userID, loginNormalized}) => {
  if (!Array.isArray(accounts)) {
    return -1;
  }
  if (userID) {
    const index = accounts.findIndex(item => item && item.userID && String(item.userID) === String(userID));
    if (index !== -1) {
      return index;
    }
  }
  if (loginNormalized) {
    return accounts.findIndex(item => item && item.loginNormalized === loginNormalized);
  }
  return -1;
};

const mergeDefined = (target, patch) => {
  const next = {...target};
  if (!patch || typeof patch !== 'object') {
    return next;
  }
  Object.keys(patch).forEach(key => {
    if (patch[key] !== undefined) {
      next[key] = patch[key];
    }
  });
  return next;
};

const readLegacySnapshot = () => {
  if (!canUseStorage()) {
    return {...LEGACY_DEFAULTS, accountServices: null, login: ''};
  }
  return {
    token: localStorage.token || '',
    userID: localStorage.userID || '',
    userName: localStorage.userName || LEGACY_DEFAULTS.userName,
    userType: localStorage.userType || LEGACY_DEFAULTS.userType,
    mainCss: localStorage.mainCss || LEGACY_DEFAULTS.mainCss,
    agreementNumber: localStorage.agreementNumber || '',
    personalAccount: localStorage.personalAccount || '',
    chatBadge: localStorage.chatBadge || LEGACY_DEFAULTS.chatBadge,
    notificationBadge: localStorage.notificationBadge || LEGACY_DEFAULTS.notificationBadge,
    chatActive: localStorage.chatActive || LEGACY_DEFAULTS.chatActive,
    agreementAccepted: localStorage.agreementAccepted || LEGACY_DEFAULTS.agreementAccepted,
    accountServices: localStorage.accountServices || null,
    login: ''
  };
};

const buildAccountFromLegacy = () => {
  const legacy = readLegacySnapshot();
  const loginNormalized = normalizeLogin(legacy.login);
  return {
    id: generateAccountId({userID: legacy.userID, loginNormalized}),
    login: legacy.login || '',
    loginNormalized,
    token: legacy.token || '',
    userID: legacy.userID || '',
    userName: legacy.userName || LEGACY_DEFAULTS.userName,
    userType: legacy.userType || LEGACY_DEFAULTS.userType,
    mainCss: legacy.mainCss || LEGACY_DEFAULTS.mainCss,
    agreementNumber: legacy.agreementNumber || '',
    personalAccount: legacy.personalAccount || '',
    chatBadge: legacy.chatBadge || LEGACY_DEFAULTS.chatBadge,
    notificationBadge: legacy.notificationBadge || LEGACY_DEFAULTS.notificationBadge,
    chatActive: legacy.chatActive || LEGACY_DEFAULTS.chatActive,
    agreementAccepted: legacy.agreementAccepted || LEGACY_DEFAULTS.agreementAccepted,
    accountServices: legacy.accountServices || null,
    createdAt: Date.now(),
    lastActiveAt: Date.now()
  };
};

export const applyAccountToLegacy = (account) => {
  if (!canUseStorage()) {
    return;
  }
  const resolved = account || {};
  localStorage.token = resolved.token || '';
  localStorage.userID = resolved.userID || '';
  localStorage.userName = resolved.userName || LEGACY_DEFAULTS.userName;
  localStorage.userType = resolved.userType || LEGACY_DEFAULTS.userType;
  localStorage.mainCss = resolved.mainCss || LEGACY_DEFAULTS.mainCss;
  localStorage.agreementNumber = resolved.agreementNumber || '';
  localStorage.personalAccount = resolved.personalAccount || '';
  localStorage.chatBadge = resolved.chatBadge || LEGACY_DEFAULTS.chatBadge;
  localStorage.notificationBadge = resolved.notificationBadge || LEGACY_DEFAULTS.notificationBadge;
  localStorage.chatActive = resolved.chatActive || LEGACY_DEFAULTS.chatActive;
  localStorage.agreementAccepted = resolved.agreementAccepted || LEGACY_DEFAULTS.agreementAccepted;
  if (resolved.accountServices) {
    localStorage.accountServices = resolved.accountServices;
  } else {
    localStorage.removeItem('accountServices');
  }
};

export const clearLegacySession = () => {
  if (!canUseStorage()) {
    return;
  }
  localStorage.token = '';
  localStorage.userID = '';
  localStorage.userName = LEGACY_DEFAULTS.userName;
  localStorage.userType = LEGACY_DEFAULTS.userType;
  localStorage.mainCss = LEGACY_DEFAULTS.mainCss;
  localStorage.agreementNumber = '';
  localStorage.personalAccount = '';
  localStorage.chatBadge = LEGACY_DEFAULTS.chatBadge;
  localStorage.notificationBadge = LEGACY_DEFAULTS.notificationBadge;
  localStorage.chatActive = LEGACY_DEFAULTS.chatActive;
  localStorage.agreementAccepted = LEGACY_DEFAULTS.agreementAccepted;
  localStorage.removeItem('accountServices');
};

export const ensureActiveAccount = () => {
  if (!canUseStorage()) {
    return null;
  }
  const accounts = readAccounts();
  if (accounts.length === 0) {
    if (localStorage.token) {
      const legacyAccount = buildAccountFromLegacy();
      writeAccounts([legacyAccount]);
      setActiveAccountId(legacyAccount.id);
      applyAccountToLegacy(legacyAccount);
      return legacyAccount;
    }
    return null;
  }
  let activeId = getActiveAccountId();
  let activeAccount = accounts.find(item => item.id === activeId);
  if (!activeAccount) {
    activeAccount = accounts.slice().sort((a, b) => (b.lastActiveAt || 0) - (a.lastActiveAt || 0))[0];
    activeId = activeAccount ? activeAccount.id : null;
    setActiveAccountId(activeId);
  }
  if (activeAccount) {
    applyAccountToLegacy(activeAccount);
  }
  return activeAccount || null;
};

export const getAccountsSnapshot = () => {
  const accounts = readAccounts();
  const activeId = getActiveAccountId();
  const sorted = accounts.slice().sort((a, b) => {
    if (a.id === activeId) {
      return -1;
    }
    if (b.id === activeId) {
      return 1;
    }
    return (b.lastActiveAt || 0) - (a.lastActiveAt || 0);
  });
  return {accounts: sorted, activeId};
};

export const addOrUpdateAccount = ({token, login, userID} = {}) => {
  if (!canUseStorage()) {
    return {account: null, isNew: false};
  }
  if (!token) {
    return {account: null, isNew: false};
  }
  const accounts = readAccounts();
  const loginNormalized = normalizeLogin(login);
  const index = findAccountIndex(accounts, {userID, loginNormalized});
  const now = Date.now();
  const baseAccount = index === -1
      ? {
        id: generateAccountId({userID, loginNormalized}),
        createdAt: now
      }
      : accounts[index];
  const nextAccount = mergeDefined(baseAccount, {
    token,
    login,
    loginNormalized,
    userID,
    lastActiveAt: now
  });

  if (index === -1) {
    accounts.push(nextAccount);
  } else {
    accounts[index] = nextAccount;
  }
  setActiveAccountId(nextAccount.id);
  writeAccounts(accounts);
  applyAccountToLegacy(nextAccount);
  return {account: nextAccount, isNew: index === -1};
};

export const updateActiveAccount = (patch = {}) => {
  if (!canUseStorage()) {
    return null;
  }
  const accounts = readAccounts();
  const activeId = getActiveAccountId();
  if (!activeId) {
    return null;
  }
  const index = accounts.findIndex(item => item.id === activeId);
  if (index === -1) {
    return null;
  }
  const updated = mergeDefined(accounts[index], patch);
  accounts[index] = updated;
  writeAccounts(accounts);
  return updated;
};

export const syncActiveAccountFromLegacy = () => {
  if (!canUseStorage()) {
    return null;
  }
  if (!localStorage.token) {
    return null;
  }
  const snapshot = readLegacySnapshot();
  return updateActiveAccount({
    token: snapshot.token || undefined,
    userID: snapshot.userID || '',
    userName: snapshot.userName,
    userType: snapshot.userType,
    mainCss: snapshot.mainCss,
    agreementNumber: snapshot.agreementNumber,
    personalAccount: snapshot.personalAccount,
    chatBadge: snapshot.chatBadge,
    notificationBadge: snapshot.notificationBadge,
    chatActive: snapshot.chatActive,
    agreementAccepted: snapshot.agreementAccepted,
    accountServices: snapshot.accountServices
  });
};

export const saveAccountServices = (services) => {
  if (!canUseStorage()) {
    return;
  }
  if (!services || typeof services !== 'object') {
    localStorage.removeItem('accountServices');
    updateActiveAccount({accountServices: null});
    return;
  }
  try {
    const serialized = JSON.stringify(services);
    localStorage.accountServices = serialized;
    updateActiveAccount({accountServices: serialized});
  } catch (error) {
    localStorage.removeItem('accountServices');
    updateActiveAccount({accountServices: null});
  }
};

export const switchAccount = (accountId) => {
  if (!canUseStorage()) {
    return {ok: false};
  }
  const accounts = readAccounts();
  const index = accounts.findIndex(item => item.id === accountId);
  if (index === -1) {
    return {ok: false};
  }
  const nextAccount = {
    ...accounts[index],
    lastActiveAt: Date.now()
  };
  accounts[index] = nextAccount;
  setActiveAccountId(nextAccount.id);
  writeAccounts(accounts);
  applyAccountToLegacy(nextAccount);
  return {ok: true, account: nextAccount};
};

export const removeAccount = (accountId) => {
  if (!canUseStorage()) {
    return {ok: false};
  }
  const accounts = readAccounts();
  const index = accounts.findIndex(item => item.id === accountId);
  if (index === -1) {
    return {ok: false};
  }
  const wasActive = accountId === getActiveAccountId();
  accounts.splice(index, 1);
  writeAccounts(accounts);
  if (wasActive) {
    if (accounts.length > 0) {
      const nextAccount = accounts.slice().sort((a, b) => (b.lastActiveAt || 0) - (a.lastActiveAt || 0))[0];
      setActiveAccountId(nextAccount.id);
      applyAccountToLegacy(nextAccount);
      return {ok: true, switched: true, hasAccounts: true, activeId: nextAccount.id};
    }
    setActiveAccountId(null);
    clearLegacySession();
    return {ok: true, switched: false, hasAccounts: false};
  }
  return {ok: true, switched: false, hasAccounts: accounts.length > 0};
};

export const handleAuthFailure = ({router, reloadOnSwitch = true, fallbackRoute = '/login'} = {}) => {
  const activeId = getActiveAccountId();
  if (!activeId) {
    clearLegacySession();
    if (router) {
      router.push(fallbackRoute);
    }
    return {switched: false};
  }
  const result = removeAccount(activeId);
  if (result && result.hasAccounts) {
    if (router) {
      router.push('/');
    }
    if (reloadOnSwitch && typeof window !== 'undefined' && window.location) {
      window.location.reload();
    }
    return {switched: true};
  }
  if (router) {
    router.push(fallbackRoute);
  }
  return {switched: false};
};
